import { defineStore } from 'pinia';
import { NOTIFICATION_SOUND } from '@/config/static-images';
import { useDialoguesStore } from './dialogues';
import { useImStore } from './im';
import { useTicketActive } from './ticketActive';
import { useTokenStore } from './token';
import { useUserStore } from './user';

interface SocketState {
  socketTask: UniApp.SocketTask | null
  isConnected: boolean
  reconnectCount: number
}

export const useSocketStore = defineStore('socket', {
  state: (): SocketState => ({
    socketTask: null,
    isConnected: false,
    reconnectCount: 0,
  }),
  actions: {
    initSocket() {
      const tokenStore = useTokenStore();

      if (!tokenStore.validToken) {
        console.warn('Socket init failed: User not logged in');
        return;
      }

      if (this.socketTask && this.isConnected) {
        console.log('Socket already connected');
        return;
      }

      const wsUrl = import.meta.env.VITE_WS_URL || 'wss://your-ws-url';

      this.socketTask = uni.connectSocket({
        url: `${wsUrl}?token=userToken_${tokenStore.validToken}`,
        complete: () => {},
      });

      this.socketTask.onOpen(() => {
        console.log('WebSocket Connected');
        this.isConnected = true;
        this.reconnectCount = 0;
        this.getKefu();
      });

      this.socketTask.onMessage((res) => {
        const { event: type, content: data } = JSON.parse(res.data);
        const dialoguesStore = useDialoguesStore();
        const ticketStore = useTicketActive();

        switch (type) {
          case 'event_all_unReply_room':
            if (data.unReply.length > 0) {
              data.unReply.forEach((item: any) => {
                dialoguesStore.addDialogues(item['session-chat']);
              });
            }
            break;
          case 'event_kefu_conn':
            dialoguesStore.addDialogues(data.data['session-chat']);
            break;
          case 'event_talk':
            dialoguesStore.addMessageToDialoguesList(data.data);
            break;
          case 'event_talk_revoke':
            dialoguesStore.delMessageFromDialoguesList(data.data);
            break;
          case 'event_kefu_switch':
            this.handleKefuSwitch(data);
            break;
          case 'event_send_ticket_msg':
            ticketStore.addTicketChatItem(data.data);
            break;
          case 'event_kefu_get_ticket':
            ticketStore.addTicket(data.data);
            break;
          case 'event_revoke_ticket_msg':
            ticketStore.delTicketChatItemFormChatList(data.data);
            break;
          case 'event_update_session_id':
            dialoguesStore.updateSessionChatId(data.old_id, data.new_id);
            break;
          case 'event_online_kefu':
            {
              const { isClient } = useUserStore() as any;
              if (isClient) dialoguesStore.setKfNum(data);
            }
            break;
          case 'event_clear_unReply_num':
            dialoguesStore.setUnreadNum(data.receiver_id, 0);
            break;
          case 'im_room':
            if (useUserStore().isClient) {
              useImStore().onRoomEvent();
            }
            break;
          case 'im_msg':
            {
              const userStore = useUserStore();
              if (userStore.isClient) {
                const mine = data?.data?.sender_id === userStore.id && data?.data?.sender_type === 'user';
                if (!mine) {
                  this.playNotificationSound();
                }
                useImStore().onMessageEvent(data);
              }
            }
            break;
          case 'im_read':
            if (useUserStore().isClient) {
              useImStore().onReadEvent(data);
            }
            break;
          default:
            console.log('Unknown WS event:', type, data);
        }
      });

      this.socketTask.onClose(() => {
        console.log('WS Closed, reconnecting...');
        this.isConnected = false;
        this.socketTask = null;
        if (this.reconnectCount < 10) {
          this.reconnectCount++;
          setTimeout(() => this.initSocket(), 3000);
        }
      });

      this.socketTask.onError((err) => {
        console.error('WS Error:', err);
        this.isConnected = false;
      });
    },

    handleKefuSwitch(data: any) {
      const userStore = useUserStore() as any;
      const dialoguesStore = useDialoguesStore();
      const { id, isClient } = userStore;

      const { mobile, site_name: siteName } = data['session-chat'];
      const sessionName = `${mobile}[${siteName}]`;

      if (id === data.new_kefu_id) {
        dialoguesStore.addDialogues(data['session-chat']);
        dialoguesStore.setActive(data['session-chat'].id);
      }
      else if (id === data.old_kefu_id) {
        dialoguesStore.delDialogues(data['session-chat'].id);
      }
      else if (isClient) {
        console.log('会话转移通知');
      }
    },

    getKefu() {
      if (this.socketTask && this.isConnected) {
        this.socketTask.send({
          data: '{"event":"event_online_kefu", "content":""}',
        });
        setTimeout(() => this.getKefu(), 1000 * 20);
      }
    },

    playNotificationSound() {
      if (uni.getStorageSync('push_sound_enabled') === false) return;
      const innerAudioContext = uni.createInnerAudioContext();
      innerAudioContext.autoplay = true;
      innerAudioContext.src = NOTIFICATION_SOUND;
      innerAudioContext.onPlay(() => {
        console.log('开始播放提示音');
      });
      innerAudioContext.onError((res) => {
        console.error('播放提示音失败:', res.errMsg);
      });
      innerAudioContext.onEnded(() => {
        innerAudioContext.destroy();
      });
    },

    closeSocket() {
      if (this.socketTask) {
        this.socketTask.close({});
        this.socketTask = null;
        this.isConnected = false;
      }
    },
  },
});
